import { Tag } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';

import { useGetMedicalDocumentsByClientQuery } from '@app/api/MedicalDocuments';
import type { ModelMedicalDocumentResponse } from '@app/api/MedicalDocuments';
import { CDA_4_KIND } from '@app/components/RecordModal/components/MedicalCard/data';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import { getDocumentStatusLabel } from '@pages/MedicalCard/MedicalCardRegistry/types';

import type { UseMedicalConsultationHistoryProps } from '../types';
import type { ColumnsType } from 'antd/es/table';

export const useMedicalConsultationHistory = ({
  clientId,
}: UseMedicalConsultationHistoryProps) => {
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const {
    data: documents = [],
    isLoading,
    error,
  } = useGetMedicalDocumentsByClientQuery(
    { ActiveBranchId, clientId },
    { skip: !ActiveBranchId || !clientId }
  );

  useEffect(() => {
    if (error) {
      showNotification(handleCatchError(error));
    }
  }, [error, showNotification]);

  const renderKindColumn = (kind: number): React.ReactNode => {
    const kindMap: Record<number, string> = {
      [CDA_4_KIND]: 'Протокол консультации (CDA) Редакция 4',
    };

    return kindMap[kind] || `Тип ${kind}`;
  };

  const renderDoctorName = (
    record: ModelMedicalDocumentResponse
  ): React.ReactNode => {
    const doctorData = JSON.parse(record.Doctor || '{}');

    return (
      `${doctorData?.name || ''} ${doctorData?.patronymic || ''}`.trim() || '-'
    );
  };

  const columns: ColumnsType<ModelMedicalDocumentResponse> = [
    {
      title: 'Дата создания',
      dataIndex: 'CreationDateTime',
      key: 'CreationDateTime',
      width: 160,
      render: (date: string | null) =>
        date ? dayjs(date).format('DD.MM.YYYY HH:mm') : '-',
    },
    {
      title: 'Тип документа',
      dataIndex: 'KindName',
      key: 'KindName',
      render: renderKindColumn,
    },
    {
      title: 'Врач',
      key: 'DoctorName',
      width: 220,
      render: (_, record) => renderDoctorName(record),
    },
    {
      title: 'Филиал',
      dataIndex: 'BranchName',
      key: 'BranchName',
      width: 220,
      // render: (_, record) => renderDoctorName(record),
    },
    {
      title: 'Статус',
      dataIndex: 'Status',
      key: 'Status',
      width: 200,
      render: (status: string) => <Tag>{getDocumentStatusLabel(status)}</Tag>,
    },
  ];

  return { documents, isLoading, columns };
};
